import clsx from 'clsx';
import { Fragment, useRef, type CSSProperties, type ReactElement } from 'react';

import type { CompoundInputProps, CompoundInputValue } from './types';

import { typedMemo } from '../../utils/typedMemo';
import { Layout } from '../Layout/Layout';
import './CompoundInput.css';
import { useContainerWidth, useIsStacked } from './hooks';
import { CompoundInputSegmentControl } from './subcomponents/CompoundInputSegment';
import {
  resolveHugCharacters,
  resolveSegmentOrder,
  STACK_THRESHOLD_PX,
} from './utils';

function CompoundInputComponent<V extends CompoundInputValue>({
  label,
  name,
  segments,
  value,
  onChange,
  disabled,
  required,
  onKeyDown,
  status,
  stackOrder,
  __private,
}: CompoundInputProps<V>) {
  const { ariaDescribedBy, ariaLabelledBy, id } = __private ?? {};
  const accessibleName = ariaLabelledBy ? undefined : label;

  const containerRef = useRef<HTMLElement | null>(null);
  const isStacked = useIsStacked(containerRef, STACK_THRESHOLD_PX);
  const containerWidth = useContainerWidth(containerRef);
  const orderedSegments = resolveSegmentOrder(segments, stackOrder, isStacked);

  return (
    <Layout
      align="stretch"
      aria-describedby={ariaDescribedBy}
      aria-label={accessibleName}
      aria-labelledby={ariaLabelledBy}
      className={clsx(
        'eds-compound-input',
        disabled && 'eds-compound-input--disabled',
        isStacked && 'eds-compound-input--stacked',
        status && `eds-compound-input--${status}`,
      )}
      direction={isStacked ? 'column' : 'row'}
      id={id}
      ref={containerRef}
      role="group"
      spacing="none"
    >
      {orderedSegments.map((segment, index) => {
        // Only a select needs the hint: a text or number segment already
        // hugs its own content, while Select's trigger text does not.
        const hugCharacters =
          segment.width === 'hug' && segment.type === 'select'
            ? resolveHugCharacters(segment.options, segment.placeholder)
            : null;

        return (
          <Fragment key={segment.name}>
            {index > 0 && (
              <span
                aria-hidden="true"
                className="eds-compound-input--divider"
              />
            )}
            <div
              className={clsx(
                'eds-compound-input--segment',
                `eds-compound-input--segment--${segment.width}`,
                hugCharacters !== null &&
                  'eds-compound-input--segment--hug-options',
              )}
              style={
                hugCharacters === null
                  ? undefined
                  : ({ '--_hug-characters': hugCharacters } as CSSProperties)
              }
            >
              <CompoundInputSegmentControl<V>
                disabled={disabled}
                menuMinWidth={containerWidth}
                required={required}
                segment={segment}
                value={value}
                onChange={onChange}
                onKeyDown={onKeyDown}
                __private={{
                  ariaDescribedBy,
                  id: `${name}-${segment.name}`,
                }}
              />
            </div>
          </Fragment>
        );
      })}
    </Layout>
  );
}

export const CompoundInput: {
  <V extends CompoundInputValue>(props: CompoundInputProps<V>): ReactElement;
  displayName?: string;
} = typedMemo(CompoundInputComponent);

CompoundInput.displayName = 'CompoundInput';
